import clsx from 'clsx';
import React, { CSSProperties, Fragment, ReactNode } from 'react';
import { Virtuoso } from 'react-virtuoso';

export interface VirtualizedMenuListProps {
	/** The option rows react-select handed its `MenuList`. */
	children: ReactNode;
	/** react-select's own class for the slot, plus whatever `classNames.menuList` added. */
	className?: string;
	/** Pinned content above the list -- arbitrary height, see the flex note below. */
	header?: ReactNode;
	maxHeight?: number;
	/**
	 * Caps the list at N option rows so a long one scrolls instead of growing past
	 * the viewport. Omit to size the list to the full option count.
	 */
	maxVisibleOptions?: number;
	optionHeight: number;
	/** The emotion style react-select computed for the slot. */
	style?: CSSProperties;
}

/**
 * The virtualised body of BaySelect's menu, split out of BaySelect itself so the
 * sizing rules below stay in one place. Presentational only -- it takes everything
 * it needs as props, so BaySelect's react-select `MenuList` slot is a thin adapter
 * that reads them off `props`/`selectProps`.
 */
const VirtualizedMenuList = ({
	children,
	className,
	header,
	maxHeight,
	maxVisibleOptions,
	optionHeight,
	style,
}: VirtualizedMenuListProps) => {
	// `className` is where a `classNames.menuList` callback's class arrives -- merge it
	// rather than replacing it with this element's own.
	const menuListClassName = clsx('bay-select-menu-list', className);

	// With no options react-select passes ONE element (NoOptionsMessage / the loading
	// message), not an array, so `totalCount` was undefined and Virtuoso rendered
	// nothing. There is nothing to virtualise here anyway -- render it directly.
	if (!Array.isArray(children)) {
		return (
			<div className={menuListClassName} style={{ ...style, maxHeight }}>
				{header}
				{children}
			</div>
		);
	}

	// `maxVisibleOptions` is opt-in and undefined by default, so consumers that do
	// not pass it keep sizing the list to the full option count as before.
	const visibleCount = maxVisibleOptions
		? Math.min(children.length, maxVisibleOptions)
		: children.length;
	const listHeight = visibleCount * optionHeight;

	// A flex column, not a fixed height minus a fixed header offset. `header` is
	// arbitrary content, so no constant can match its real height -- too small
	// overflowed this element into a second scrollbar beside Virtuoso's own, too large
	// left dead space. `overflow: hidden` keeps Virtuoso the only thing that scrolls.
	return (
		<div
			className={menuListClassName}
			style={{
				...style,
				display: 'flex',
				flexDirection: 'column',
				height: 'auto',
				maxHeight,
				overflow: 'hidden',
			}}
		>
			{header}
			{/* `minHeight: 0` is what lets this shrink below `listHeight` -- without it a
			    flex item will not go under its content size and the overflow returns. */}
			<div style={{ flex: `0 1 ${listHeight}px`, minHeight: 0 }}>
				<Virtuoso
					itemContent={(index) => (
						<Fragment key={index}>{children[index]}</Fragment>
					)}
					style={{ height: '100%' }}
					totalCount={children.length}
				/>
			</div>
		</div>
	);
};

export default VirtualizedMenuList;
